import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

export default class extends Controller {
  static values = { url: String }

  connect() {
    this.initSortable()
  }

  initSortable() {
    this.sortable = new Sortable(this.element, {
      animation: 150,
      handle: "[data-sortable-handle]",
      ghostClass: "opacity-30",
      draggable: "[data-sortable-id]",
      // Auto-scroll the nearest scroll container (the <main> content area) while
      // dragging near its top/bottom edge, so a photo can be dragged to the top
      // of a long DPK grid in one gesture. forceAutoScrollFallback runs
      // Sortable's own continuous scroll loop instead of deferring to native
      // HTML5-DnD autoscroll, which stalls the moment the pointer is held still
      // at the edge (Chrome/Firefox) - the exact symptom Rachel hit.
      scroll: true,
      forceAutoScrollFallback: true,
      scrollSensitivity: 80,
      scrollSpeed: 12,
      bubbleScroll: true,
      // Touch: press-and-hold to drag so a quick swipe still scrolls the page
      delay: 150,
      delayOnTouchOnly: true,
      // The rows as they were before this drag, so a failed save can put
      // them back (owner 2026-09-29: errors are no longer silent, and the
      // list must not keep showing an order that did not save).
      onStart: () => { this.snapshot = Array.from(this.element.children) },
      onEnd: this.onDragEnd.bind(this)
    })
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy()
    }
  }

  async onDragEnd(event) {
    // This drag's starting rows, kept here: a second drag started before
    // this save answers replaces this.snapshot.
    const before = this.snapshot
    if (event.oldDraggableIndex === event.newDraggableIndex) return

    const items = this.element.querySelectorAll("[data-sortable-id]")

    // If no URL, update position hidden fields in the form instead
    if (!this.urlValue) {
      items.forEach((item, index) => {
        const posField = item.querySelector(".position-field")
        if (posField) posField.value = index
      })
      return
    }

    const ids = Array.from(items).map(item => item.dataset.sortableId)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ ids: ids })
      })

      if (!response.ok) throw new Error("Sort failed")
      // Announce the saved order ("sortable:sorted", bubbling) so a page that
      // shows the result elsewhere (the press release preview) can redraw.
      this.dispatch("sorted", { detail: { ids } })
    } catch (error) {
      console.error("Failed to save sort order:", error)
      // Only while this list is still on screen: a slow failure after the
      // staffer moved to another page must not pop up there.
      if (!this.element.isConnected) return
      this.putBack(this.element, before, event?.item)
      if (window.showToast) window.showToast("error", "Could not save the new order. Nothing was changed.")
    }
  }

  // Put back only the dragged row, before the first row that followed it
  // when the drag began and is still in the list. Not the whole snapshot:
  // on phones Sortable's floating copy of the row is in that snapshot (it is
  // removed on drop), and a row taken off the page meanwhile must stay off
  // (committee 2026-09-29). The other rows never moved.
  putBack(list, before, item) {
    if (!before || !item) return
    if (item.parentNode !== list) return   // taken off the page meanwhile
    const i = before.indexOf(item)
    const next = before.slice(i + 1).find((el) => el !== item && el.parentNode === list)
    list.insertBefore(item, next || null)
  }
}
